import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Table, Badge, Group, TextInput, Select,
  Center, Loader, Pagination, Stack,
} from '@mantine/core'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { IconSearch } from '@tabler/icons-react'
import { getAuditLogs } from '../api/client'

const ACTION_COLOR: Record<string, string> = {
  LOGIN: 'blue', LOGOUT: 'gray', VIEW: 'gray',
  CREATE: 'teal', UPDATE: 'yellow', DELETE: 'red',
  APPROVE: 'teal', REJECT: 'orange', UPLOAD: 'violet', EXPORT: 'cyan',
}
const ACTIONS = ['LOGIN','LOGOUT','VIEW','CREATE','UPDATE','DELETE','APPROVE','REJECT','UPLOAD','EXPORT']

export default function AuditLogPage() {
  const { t } = useTranslation()
  const { isMobile, isSmall } = useBreakpoint()
  const [logs, setLogs]             = useState<any[]>([])
  const [total, setTotal]           = useState(0)
  const [page, setPage]             = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading]       = useState(true)
  const [filters, setFilters]       = useState({ username: '', action: '', resource: '' })

  const actionLabel = (a: string) => t(`auditLog.actionLabel.${a}`, a)
  const resourceLabel = (r: string) => t(`auditLog.resourceLabel.${r}`, r)

  const load = async (p = 1) => {
    setLoading(true)
    try {
      const params: any = { page: p, limit: 50 }
      if (filters.username) params.username = filters.username
      if (filters.action)   params.action   = filters.action
      if (filters.resource) params.resource = filters.resource
      const r = await getAuditLogs(params)
      setLogs(r.data.data ?? [])
      setTotal(r.data.total ?? 0)
      setTotalPages(r.data.totalPages ?? 1)
    } catch { setLogs([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { setPage(1); load(1) }, [filters])
  useEffect(() => { load(page) }, [page])

  const f = (k: string) => (v: any) => setFilters(p => ({ ...p, [k]: v ?? '' }))

  return (
    <Box p={isSmall ? 16 : 32}>
      <Box mb="xl">
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
          {t('auditLog.title')}
        </Text>
        <Text size="sm" c="dimmed">{t('auditLog.subtitle')} — {total.toLocaleString()} {t('auditLog.recordsCount')}</Text>
      </Box>

      <Group mb="lg" gap="sm" wrap="wrap">
        <TextInput
          placeholder={t('auditLog.filterByUserPlaceholder')}
          leftSection={<IconSearch size={14} />}
          value={filters.username}
          onChange={e => f('username')(e.target.value)}
          style={{ width: isMobile ? '100%' : 200 }}
          styles={{ input: { borderColor: '#e2e8f0', fontSize: 13 } }}
        />
        <Select
          placeholder={t('auditLog.filterByActionPlaceholder')}
          clearable
          data={ACTIONS.map(a => ({ value: a, label: actionLabel(a) }))}
          value={filters.action || null}
          onChange={f('action')}
          style={{ width: isMobile ? '100%' : 160 }}
          styles={{ input: { borderColor: '#e2e8f0', fontSize: 13 } }}
        />
        {!isMobile && (
          <TextInput
            placeholder={t('auditLog.filterByResourcePlaceholder')}
            value={filters.resource}
            onChange={e => f('resource')(e.target.value)}
            style={{ width: 160 }}
            styles={{ input: { borderColor: '#e2e8f0', fontSize: 13 } }}
          />
        )}
      </Group>

      <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        {loading ? <Center p="xl"><Loader color="teal" size="sm" /></Center> : (
          isMobile ? (
            <Stack gap={0}>
              {logs.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t('common.noRecords')}</Text></Center>
              ) : logs.map(log => (
                <Box key={log.id} p="md" style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <Group justify="space-between" mb={4}>
                    <Badge color={ACTION_COLOR[log.action] ?? 'gray'} variant="light" size="sm">{actionLabel(log.action)}</Badge>
                    <Badge color={log.success ? 'teal' : 'red'} variant="light" size="xs">
                      {log.success ? t('auditLog.statusOk') : t('auditLog.statusFail')}
                    </Badge>
                  </Group>
                  <Text size="sm" fw={500}>{log.username ?? '-'} → {resourceLabel(log.resource)}</Text>
                  <Text size="xs" c="dimmed" mt={2}>{log.detail?.slice(0, 80) ?? '-'}</Text>
                  <Text size="xs" c="dimmed" mt={2}>{new Date(log.createdAt).toLocaleString('th-TH')}</Text>
                </Box>
              ))}
            </Stack>
          ) : (
            <Table highlightOnHover>
              <Table.Thead style={{ background: '#f8fafc' }}>
                <Table.Tr>
                  {[
                    t('auditLog.timestamp'), t('auditLog.user'), t('auditLog.action'),
                    t('auditLog.resource'), t('auditLog.detail'), t('auditLog.ip'), t('common.status'),
                  ].map(h => (
                    <Table.Th key={h} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logs.length === 0 ? (
                  <Table.Tr><Table.Td colSpan={7}><Center p="xl"><Text c="dimmed" size="sm">{t('common.noRecords')}</Text></Center></Table.Td></Table.Tr>
                ) : logs.map(log => (
                  <Table.Tr key={log.id}>
                    <Table.Td><Text size="xs" c="dimmed">{new Date(log.createdAt).toLocaleString('th-TH')}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{log.username ?? '-'}</Text></Table.Td>
                    <Table.Td><Badge color={ACTION_COLOR[log.action] ?? 'gray'} variant="light" size="sm">{actionLabel(log.action)}</Badge></Table.Td>
                    <Table.Td><Text size="sm">{resourceLabel(log.resource)}{log.resourceId ? ` #${log.resourceId}` : ''}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 220 }}>{log.detail ?? '-'}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{log.ipAddress ?? '-'}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={log.success ? 'teal' : 'red'} variant="light" size="sm">
                        {log.success ? t('auditLog.statusOk') : t('auditLog.statusFail')}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )
        )}
      </Box>

      {totalPages > 1 && (
        <Group justify="center" mt="lg">
          <Pagination total={totalPages} value={page} onChange={setPage} color="teal" size="sm" />
        </Group>
      )}
    </Box>
  )
}
